import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';
import {
  cardsFromRun, mergePerformanceRecommendationCards, annotateContention, consolidateRecommendations,
} from '../../utils/performanceCards';
import { buildSuggestionEditRequest } from '../../utils/optimizerSuggestionApply';
import { resolveOptimizePlatformCampaignId } from '../../utils/optimizeCampaignRun';
import { excludeInactiveDiagnosticRuns } from '../../utils/diagnosticCampaignEligibility';
import { activeDiagnosticView } from '../../utils/inactiveDiagnosticFindings';
import { deliveryEvidence } from '../../utils/diagnosisFindings';
import { tierContext } from '../../utils/findingTier';
import { cardsFromStrategistReviews, strategistCoverage } from '../../utils/strategistCards';

const ROUTE_BASE = '/api/v2/performance';
const EMPTY_REPORTING = [];

/**
 * One GET, with its result discarded if the caller has moved on.
 *
 * Without the guard every one of these hooks setStates after unmount — switching
 * tabs or leaving the page mid-request warns in dev and leaks in production. The
 * `seq` counter (rather than a plain boolean) also drops a STALE in-flight
 * response when the campaign changes twice quickly, so the tab cannot end up
 * rendering the previous campaign's run under the current campaign's name.
 *
 * `path` is null when there is nothing to ask for — no account, no user, or the
 * account gate is off. A null path must not fetch, which is what keeps the gated
 * page from calling the API at all.
 *
 * Params go through axios rather than a hand-built query string, matching every
 * other v2 caller (`{ params: { accountId, userId } }`). BOTH are required:
 * middleware/auth.js `extractUser` reads userId from the query or body and 400s
 * without it, and the api interceptor injects only the API key — it does not add
 * userId. A hand-built string that forgot it 400s every request.
 */
export function useGuardedFetch(path, params) {
  // Seeded from `url` so a hook with nothing to ask for starts settled rather
  // than starting "loading" and being corrected a microtask later. That spurious
  // first state is what made the gated page render a loading flash it had no
  // reason to, and every consumer had to tolerate a loading:true it would never
  // see resolve.
  const requestKey = JSON.stringify([path, params || null]);
  const [state, setState] = useState({ loading: Boolean(path), error: null, data: null, requestKey });
  const seq = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // Serialised so the effect re-runs when a value changes, not when the object
  // identity does — an inline `{accountId, userId}` would refetch every render.
  const key = JSON.stringify(params || null);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!path) {
      // Synchronous: no request is in flight, so there is nothing to await and
      // nothing to guard against.
      setState((s) => (s.loading || s.error || s.data
        ? { loading: false, error: null, data: null, requestKey }
        : s));
      return;
    }
    const current = () => alive.current && seq.current === mine;

    setState((s) => ({ ...s, data: s.requestKey === requestKey ? s.data : null, requestKey, loading: true, error: null }));
    try {
      const res = await api.get(getApiUrl(path), { params: JSON.parse(key) || {} });
      if (current()) setState({ loading: false, error: null, data: res.data, requestKey });
    } catch (err) {
      if (!current()) return;
      setState({
        loading: false,
        // 403 is the account gate, not a failure — the page renders it as such.
        error: err?.response?.status === 403
          ? 'not-enabled'
          : (err?.response?.data?.error || err.message || 'Request failed'),
        data: null, requestKey,
      });
    }
  }, [path, key, requestKey]);

  useEffect(() => { load(); }, [load]);

  const currentState = state.requestKey === requestKey ? state : { loading: Boolean(path), error: null, data: null };
  return { ...currentState, reload: load };
}

/**
 * Campaigns this account can show something for.
 *
 * The section is reachable from the nav without a campaign, so it needs a picker.
 * `kinds` says which focused analyses have completed, which is what lets the
 * picker distinguish "analysed, go look" from "never analysed" instead of
 * offering every campaign as an equally likely dead end.
 */
/**
 * Reads the runs behind the Performance section and turns them into cards.
 *
 * ONE fetch of /runs feeds Diagnostics, Budget & pacing and Recommendations,
 * because contention is computed across all three at once: a card can only be
 * flagged as contending with another agent's card if the other agent's cards are
 * in hand. Fetching per tab would leave each tab blind to exactly the conflicts
 * it most needs to show.
 *
 * Nothing here starts a run. An optimizer run pulls live delivery data and costs
 * money; the Optimize Campaign button owns starting one, with a confirmation in
 * front of it. These tabs render what already completed, and say plainly when
 * nothing has.
 */
export function usePerformanceRuns(accountId, userId, campaignId = null, campaignIds = [], strictScope = false, campaignReporting = EMPTY_REPORTING) {
  const state = useGuardedFetch(
    accountId && userId ? `${ROUTE_BASE}/runs` : null,
    { accountId, userId, ...(campaignId ? { campaignId } : {}) },
  );
  const scopeKey = JSON.stringify(campaignIds);
  const data = useMemo(() => {
    if (!state.data) return state.data;
    const selected = new Set(campaignIds);
    const runs = excludeInactiveDiagnosticRuns(state.data.runs || [], campaignReporting)
      .filter(run => (!strictScope && !campaignIds.length) || selected.has(run.campaignId));
    return { ...state.data, runs, missing: ['diagnostic', 'pacing', 'performance'].filter(kind => !runs.some(run => run.analysisKind === kind)) };
    // Scope is a value, not the caller's array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data, scopeKey, strictScope, campaignReporting]);

  const cards = useMemo(() => {
    const runs = data?.runs || [];
    // The V2 strategist's reviews, where served, are the Recommendations for every
    // campaign they read: those campaigns' V1 performance cards are not built.
    const reviews = data?.strategist || [];
    const covered = strategistCoverage(reviews);
    const known = new Map();
    // Each card carries ITS OWN campaign — the feed spans the account, so the
    // chips filter across campaigns and contention is computed between them.
    const v1 = runs.flatMap((r) => {
      const meta = {
        analysisKind: r.analysisKind,
        currency: campaignReporting.find(row => (row.campaignId || row.id) === r.campaignId)?.currency || null,
        camp: r.campaignId || null,
        campName: r.campaignName || null,
        dest: r.destination || null,
        // Server-computed: what the previous run of this kind said about the
        // same finding, keyed by suggestion index.
        recurrence: r.recurrence || null,
        // Server-read: the edits the Apply button already dispatched from this
        // run, keyed by suggestion index (applying | applied | failed).
        appliedEdits: r.appliedEdits || null,
        // THE PLATFORM'S OWN CAMPAIGN ID, resolved per card in the same order
        // useCampaignRunHistory uses — the run's own stamped id first, then the
        // campaign's. Resolved HERE rather than at apply time because the feed is
        // account-wide: there is no single loaded campaign to read it off.
        pcid: r.result?.details?.campaign_id
          || r.result?.campaign_id
          || resolveOptimizePlatformCampaignId({
            platform_campaign_id: r.platformCampaignId,
            advertiser_id: r.advertiserId,
            destination: r.destination,
          })
          || null,
        messageId: r.messageId,
      };
      if (meta.pcid && r.campaignId) {
        known.set(String(meta.pcid), { id: r.campaignId, name: r.campaignName || null, destination: r.destination || null });
      }
      if (r.analysisKind === 'performance' && meta.pcid && covered.has(String(meta.pcid))) return [];
      const primary = cardsFromRun(r.result, meta);
      if (r.analysisKind !== 'performance') return primary;
      return mergePerformanceRecommendationCards(primary, {
        experiments: r.experiments,
        googleReview: r.googleReview,
        googleRecommendations: r.result?.google_recommendations?.recommendations,
        camp: meta.camp,
        campName: meta.campName,
      });
    });
    // Scoped like the runs above: a review card stays when it names a selected campaign.
    // A campaign drill-down (campaignId) narrows them the same way.
    const selected = new Set([...campaignIds, ...(campaignId ? [campaignId] : [])]);
    const scoped = (!strictScope && !selected.size) ? null : selected;
    const v2 = cardsFromStrategistReviews(reviews, known)
      .filter((card) => !scoped || scoped.has(card.camp) || card.affectedCampaigns.some((id) => scoped.has(id)));
    return annotateContention(consolidateRecommendations([...v1, ...v2]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, campaignReporting, scopeKey, strictScope, campaignId]);

  // Per-run extras, keyed by analysis kind so a tab asks for its own run's.
  // The judge runs on every focused analysis, so `suppressed` is per-kind;
  // experiments and the Google review only ever arrive on `performance`.
  const byKind = useMemo(() => {
    const out = {};
    // Rolled up across campaigns: the section is account-wide, so the suppressed
    // set, the experiments and the Google review are the union of every
    // campaign's run of that kind rather than one campaign's.
    for (const r of data?.runs || []) {
      const cur = out[r.analysisKind] || {
        suppressed: [], experiments: [], googleReview: [], googleStatus: null,
        diagnoses: [],
      };
      // The diagnostic run's own findings (health, per-entity blockers,
      // measurement) — the content the tab exists for, of which the suggestion
      // cards are only the applyable subset.
      if (r.diagnosis) {
        cur.diagnoses.push({
          campaignId: r.campaignId || null,
          campaignName: r.campaignName || null,
          ...activeDiagnosticView(r.diagnosis),
          // The run's measured delivery gap (impression share lost, spend vs
          // budget), so a DEGRADED card can say how big it is — and lead only
          // when it is material. See diagnosisFindings.healthGap.
          deliveryEvidence: deliveryEvidence(r.result),
        });
      }
      // Every row carries the campaign it came from. The rollup is account-wide,
      // so without the stamp an 83-test plan or a Google review is one
      // undifferentiated list that no filter can narrow and no reader can
      // attribute — the tabs group and scope on `_camp`, exactly as the card
      // feed does on `camp`.
      const stamp = (rows) => (rows || []).map((x) => ({
        ...x, _camp: r.campaignId || null, _campName: r.campaignName || null,
      }));
      cur.suppressed = cur.suppressed.concat(stamp(r.suppressed));
      cur.experiments = cur.experiments.concat(stamp(r.experiments));
      cur.googleReview = cur.googleReview.concat(stamp(r.googleReview));
      cur.googleStatus = cur.googleStatus || r.googleStatus || null;
      out[r.analysisKind] = cur;
    }
    return out;
  }, [data]);

  // What each CAMPAIGN's runs said about it, as opposed to what the account's
  // runs found. byKind rolls every campaign's run of a kind into one list, which
  // is right for a feed and wrong for a table with one row per campaign — the
  // Summary table needs to answer "what does this campaign's own diagnostic say"
  // per row. Keyed by campaign id, with the run's completion time so a cell can
  // say WHEN the read was taken: a quality score with no timestamp is a claim
  // about now made from a run that may be a week old.
  const byCampaign = useMemo(() => {
    const out = {};
    for (const r of data?.runs || []) {
      if (!r.campaignId) continue;
      const cur = out[r.campaignId] || {
        diagnosis: null, diagnosedAt: null, perfSummary: null, analysedAt: null,
      };
      if (r.diagnosis) {
        cur.diagnosis = activeDiagnosticView(r.diagnosis);
        cur.diagnosedAt = r.completedAt || null;
      }
      if (r.perfSummary) {
        cur.perfSummary = r.perfSummary;
        cur.analysedAt = r.completedAt || null;
      }
      out[r.campaignId] = cur;
    }
    return out;
  }, [data]);

  // Grounded pacing movement per campaign, from the server's two-run diff.
  // Rendered only on a single-campaign drill-down: account-wide, one hero
  // cannot honestly summarize many campaigns' deltas.
  const pacingDeltas = useMemo(() => {
    const out = {};
    for (const r of data?.runs || []) {
      if (r.analysisKind === 'pacing' && r.pacingDelta && r.campaignId) {
        out[r.campaignId] = r.pacingDelta;
      }
    }
    return out;
  }, [data]);

  // What the MAIN/More tiering reads across runs — account and campaign daily
  // spend, pacing reads, impression share lost — built once here so every tab,
  // badge and summary tiers against the same figures. See utils/findingTier.js.
  //
  // The ACCOUNT total reads every run the account returned (state.data, before
  // the page's campaign scope), so the account-wide page, a scoped selection
  // and the workspace summary share one bar. A drill-down (`campaignId`) is
  // fetched server-side for that campaign only: the account total is unknown
  // there, and no card is promoted by size rather than every card.
  const tiering = useMemo(
    () => tierContext(data?.runs || [], byKind.diagnostic?.diagnoses || [], {
      accountRuns: campaignId ? [] : (state.data?.runs || []),
    }),
    [data, byKind, campaignId, state.data],
  );

  return {
    ...state,
    data,
    cards,
    byKind,
    tierContext: tiering,
    byCampaign,
    pacingDeltas,
    campaign: state.data?.campaign || null,
    // The campaigns the feed actually represents, for its chips.
    campaigns: state.data?.campaigns || [],
    runs: data?.runs || [],
    allRuns: state.data?.runs || [],
    // Which of the three analyses have never completed. "No findings" and
    // "never run" are different answers and the tabs must not conflate them.
    missing: data?.missing || [],
    // Whether this caller may run the analyses. Served by the route rather than
    // derived here: the permission rule has one definition, on the server.
    canRefresh: state.data?.canRefresh === true,
  };
}


/**
 * Active dismissals for the account, and the writes that change them.
 *
 * Fetched once and applied to every tab's cards, because a dismissal's scope can
 * reach past the campaign it was made on ("All campaigns, this check") — a
 * per-tab fetch would let a card the user dismissed account-wide reappear on the
 * tab next door.
 *
 * The reason SETS come from the server with the list, so the copy a person reads
 * is the copy that gets stored. Duplicating them here would let the two drift.
 */
export function useDismissals(accountId, userId, campaignId = null) {
  const state = useGuardedFetch(
    accountId && userId ? `${ROUTE_BASE}/dismissals` : null,
    { accountId, userId, ...(campaignId ? { campaignId } : {}) },
  );
  const [pending, setPending] = useState(0);   // bumps to force a refetch

  const dismissals = state.data?.dismissals || [];
  const reasonSets = state.data?.reasonSets || {};

  const dismiss = useCallback(async (card, { reason, note, scope, duration }) => {
    // WHICH CAMPAIGN THIS DISMISSAL BELONGS TO, in the order that is true:
    //
    //   1. an explicit `campaignId` key — how a card declares itself
    //      ACCOUNT-LEVEL. A Moments trigger sets `campaignId: null` because it
    //      belongs to the account, not to whichever campaign is open behind the
    //      tab; without this, dismissing a trigger while viewing campaign X
    //      stores it against X and the moment returns the moment you open Y.
    //      `in` rather than `??`, so an explicit null is honoured.
    //   2. `card.camp` — the campaign the card's own run came from. This is the
    //      key cardsFromRun actually writes, and reading `card.campaignId` here
    //      (a key no card has) is what broke "This campaign, this check": the id
    //      never reached the body, the route refused the scope for want of one,
    //      and the panel's only visible response was that nothing happened.
    //   3. the campaign the page is scoped to, if any.
    const cardCampaignId = ('campaignId' in card)
      ? card.campaignId
      : (card.camp || campaignId || null);
    const res = await api.post(getApiUrl(`${ROUTE_BASE}/dismissals`), {
      accountId,
      // extractUser reads userId from the body on a POST. Without it the write
      // 400s before it reaches the route.
      userId,
      campaignId: cardCampaignId,
      agent: card.agent,
      action: card.action,
      targetType: card.tt,
      targetId: card.tid,
      targetName: card.tn,
      headline: card.head || card.plain,
      reason, note, scope, duration,
    });
    setPending((n) => n + 1);
    return res.data?.dismissal;
  }, [accountId, userId, campaignId]);

  const reinstate = useCallback(async (id) => {
    // DELETE has no body, so both ride the query string.
    await api.delete(getApiUrl(`${ROUTE_BASE}/dismissals/${id}`), {
      params: { accountId, userId },
    });
    setPending((n) => n + 1);
  }, [accountId, userId]);

  // Refetch after a write so the suppression list is current without a reload.
  useEffect(() => { if (pending) state.reload(); }, [pending]);   // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, dismissals, reasonSets, dismiss, reinstate };
}

/**
 * Apply one card's suggestion.
 *
 * Dispatches through `POST /api/v2/campaigns/:id/edit` — the SAME long-standing
 * path the campaign grid, the campaign details page and the optimization agent
 * use, with the same (optimizeMessageId, suggestionIndex) tagging that makes
 * "already applied" survive a reload (/runs reads it back as `appliedEdits`).
 * This section adds a place to read the suggestions from, not a second way to
 * mutate a live campaign; there is exactly one, and it predates these tabs.
 *
 * Resolves to `{ ok, messageId }` on a dispatched edit so the feed can move the
 * card into its Applied section at once, and to nothing when no edit was sent.
 *
 * The request is built by buildSuggestionEditRequest from the UNTOUCHED
 * suggestion, never from the flattened card. That builder is the single
 * implementation for a reason: the two copies that preceded it drifted, and one
 * of them silently turned exact-match keywords into broad.
 *
 * A card without `auto` is not dispatchable. The check is here as well as in the
 * button because the button is an affordance and this is the call site — and the
 * value-level gate behind `auto_applicable` exists because an advisory
 * suggestion dressed in an executable action type once set a daily budget to 14.
 */
export function useApplySuggestion({ accountId, campaignId, destination, userId, buildEditRequest = buildSuggestionEditRequest }) {
  const [applying, setApplying] = useState({});
  const [notice, setNotice] = useState(null);

  const apply = useCallback(async (card) => {
    if (!card?.auto) {
      setNotice({ ok: false, text: 'This one is advisory — it has to be made in the platform.' });
      return;
    }
    // THE CARD'S OWN CAMPAIGN AND PLATFORM, not the page's.
    //
    // The feed is account-wide: /performance has no campaignId in the URL and
    // `runs.campaign` is null, so both page-level values are undefined there.
    // This read `campaignId` and returned early on it — silently, with no
    // notice — so Apply on the account-wide page did nothing at all and said
    // nothing about why. `destination` was null on the same page, which would
    // have mis-shaped the edit even if the guard had passed.
    const campId = card.camp || campaignId || null;
    const dest = card.dest || destination || null;
    if (!card.raw) return;
    if (!campId) {
      setNotice({ ok: false, text: 'This card has no campaign behind it, so there is nothing to apply to.' });
      return;
    }

    // An in-flight click is not a new request to mutate the campaign, so do not
    // show a second confirmation dialog for it.
    if (applying[card.id]) return;

    const confirmed = window.confirm(
      `Apply this ${String(card.action || 'recommended').replace(/_/g, ' ')} change${card.rec ? ` to ${card.rec}` : ''} to the live campaign?`,
    );
    if (!confirmed) return;

    // ONE EDIT RUN PER CLICK. The button gave no feedback while the POST was in
    // flight, so a planner who clicked twice because nothing had happened
    // dispatched two edit runs against a live DSP campaign. Guarding here rather
    // than only on the button covers every caller of apply(), present and
    // future; `applying` is in the dep list below so this reads current state
    // rather than a stale closure.
    setApplying((a) => ({ ...a, [card.id]: true }));
    setNotice(null);
    try {
      // `card.raw.platform_campaign_id` was the only source here, and a
      // suggestion does not carry one — so this was undefined on every card, and
      // MadHive's entity context (the one platform that reads it) went out with
      // no campaign_id. Named rather than silently omitted: an edit dispatched
      // against no campaign is worse than one that refuses to dispatch.
      const platformId = card.raw.platform_campaign_id || card.pcid || null;
      if (!platformId) throw new Error('Platform campaign ID not found for this campaign');

      const built = buildEditRequest(card.raw, {
        destination: dest,
        platformCampaignId: platformId,
      });
      if (built.error) throw new Error(built.error);

      const resp = await api.post(getApiUrl(`/api/v2/campaigns/${campId}/edit`), {
        // Sent alongside the internal id, as every other apply path sends it.
        // The route needs both: the UUID names the row, the platform id names
        // the thing being edited at the DSP.
        platformCampaignId: platformId,
        description: built.description,
        // The batch path applies every edit, not just the first.
        ...(built.edits ? { edits: built.edits } : {}),
        // Google Ads creative operations ride through as a typed payload
        // rather than as prose for the editor's LLM to re-parse.
        ...(built.configPayload ? { configPayload: built.configPayload } : {}),
        targetEntities: built.targetEntities,
        entityContext: built.entityContext,
        suggestedValues: built.suggestedValues,
        accountId,
        userId,
        suggestionIndex: card.index,
        optimizeMessageId: card.messageId,
      });
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Failed to create edit message');
      setNotice({ ok: true, text: 'Edit run started' });
      return { ok: true, messageId: resp.data?.messageId || null };
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.error || e.message });
      return { ok: false };
    } finally {
      setApplying((a) => { const n = { ...a }; delete n[card.id]; return n; });
    }
  }, [accountId, campaignId, destination, userId, applying, buildEditRequest]);

  return { apply, applying, notice, clearNotice: () => setNotice(null) };
}

const performanceHooks = {
  usePerformanceRuns, useApplySuggestion, useDismissals,
};

export default performanceHooks;


/**
 * Run the three analyses on demand, for whatever the page is scoped to.
 *
 * The section used to be fed by a 03:00 cron; nothing schedules it now, so this
 * is how analyses happen. The server calls the same scheduler tick the cron did,
 * which means every guard still applies — a campaign the DSP says is paused is
 * still skipped, and a caller cannot dispatch past the per-account cap.
 *
 * `force` is offered rather than assumed. The server skips any campaign analysed
 * in the last 20 hours, which is invisible from here: without the option, a press
 * shortly after a run dispatches nothing and reads as a broken button. With it,
 * the user chooses between "refresh what is stale" and a deliberate re-spend.
 */
/** How often to re-read /runs while a refresh's analyses are landing. The
 *  analyses take minutes each; 15s is frequent enough to feel live and rare
 *  enough that a client leaving the tab open costs nothing worth noticing. */
const REFRESH_POLL_MS = 15_000;
/** Give up telling the user "still running" after this long. An analysis that
 *  has not completed in 20 minutes has either failed or timed out server-side,
 *  and the honest line is the partial count, not a spinner. */
const REFRESH_DEADLINE_MS = 20 * 60 * 1000;
/** completedAt is the server's clock and pressedAt is the browser's. A minute
 *  of tolerance keeps a slightly-slow client from never counting a completion
 *  that landed a few seconds after the press. */
const CLOCK_SKEW_MS = 60 * 1000;

export function useRefreshAnalyses({ accountId, userId, campaignId, campaignIds = [], runs = [], reload = null }) {
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);
  // What the last press set in motion, and how much of it has landed. Null
  // until a press dispatches something.
  const [progress, setProgress] = useState(null);
  const timers = useRef({ poll: null, deadline: null });
  const scopeKey = JSON.stringify(campaignIds);

  const stopPolling = useCallback(() => {
    if (timers.current.poll) clearInterval(timers.current.poll);
    if (timers.current.deadline) clearTimeout(timers.current.deadline);
    timers.current = { poll: null, deadline: null };
  }, []);

  // Never leave a timer running past the page.
  useEffect(() => stopPolling, [stopPolling]);

  const refresh = useCallback(async ({ force = false } = {}) => {
    stopPolling();
    setProgress(null);
    setRefreshing(true);
    setResult(null);
    try {
      const res = await api.post(getApiUrl(`${ROUTE_BASE}/refresh`), {
        accountId,
        // extractUser reads userId from the body on a POST, the same way the
        // dismissal writes above do.
        userId,
        ...(campaignId ? { campaignId } : campaignIds.length ? { campaignIds } : {}),
        force,
      });
      setResult({ ok: true, ...res.data });

      // The analyses dispatch asynchronously and land over minutes. Someone who
      // pressed the button and is waiting on the page should be told when they
      // have all arrived, so re-read /runs on an interval and count completions
      // newer than the press until the count reaches what was dispatched.
      const dispatched = Number(res.data?.dispatched) || 0;
      if (dispatched > 0 && typeof reload === 'function') {
        const pressedAt = Date.now();
        setProgress({ pressedAt, dispatched, completed: 0, done: false, timedOut: false,
          campaignIds: campaignId ? [campaignId] : [...campaignIds] });
        timers.current.poll = setInterval(() => { reload(); }, REFRESH_POLL_MS);
        timers.current.deadline = setTimeout(() => {
          stopPolling();
          setProgress((p) => (p && !p.done ? { ...p, timedOut: true } : p));
        }, REFRESH_DEADLINE_MS);
      }
      return res.data;
    } catch (err) {
      // A 409 means a refresh is already in flight — a real answer, not a
      // failure, so it carries the server's sentence rather than a generic one.
      setResult({
        ok: false,
        error: err?.response?.data?.error || 'Failed to refresh analyses',
      });
      return null;
    } finally {
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, userId, campaignId, scopeKey, reload, stopPolling]);

  // Each time the rows change, count how many landed since the press. /runs
  // returns only COMPLETED runs, latest per (campaign, analysis), so a row newer
  // than the press is exactly one dispatched analysis having finished.
  useEffect(() => {
    if (!progress || progress.done || progress.timedOut) return;
    const since = progress.pressedAt - CLOCK_SKEW_MS;
    const completed = (runs || []).filter((r) => {
      const t = r?.completedAt ? new Date(r.completedAt).getTime() : NaN;
      return Number.isFinite(t) && t >= since
        && (!progress.campaignIds?.length || progress.campaignIds.includes(r.campaignId));
    }).length;
    const done = completed >= progress.dispatched;
    if (completed === progress.completed && !done) return;
    if (done) stopPolling();
    setProgress((p) => (p ? { ...p, completed, done } : p));
  }, [runs, progress, stopPolling]);

  const clearResult = useCallback(() => {
    stopPolling();
    setResult(null);
    setProgress(null);
  }, [stopPolling]);

  return { refresh, refreshing, result, clearResult, progress };
}
